import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { login } from '../../services/core/auth';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';

import IconButton from '@mui/material/IconButton';
import { Clear } from '@mui/icons-material';
import { GlobalUtilContext } from '../../core/contexts';
import { Typography, AppBar, Toolbar } from '@mui/material';
import NSLogo from '../../assets/ns_logo.svg';

export function Login() {
  const { t } = useTranslation();
  const { serviceName } = useParams();
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState('');
  const { setIsLoading, setIsLoggedIn } = useContext(GlobalUtilContext);

  const handleClearClick = useCallback(() => {
    setAccessToken('');
    setError('');
  }, []);

  const handleChange = useCallback(
    (newValue) => {
      setAccessToken(newValue.target.value);
      if (error) {
        setError('');
      }
    },
    [error]
  );

  const handleLogin = useCallback(
    (keyphrase) => {
      const loginRequest = async () => {
        setIsLoading(true);
        const res = await login({ username: serviceName, password: keyphrase || accessToken });

        if (res?.access_token) {
          setError('');
          setIsLoggedIn(true);
          localStorage.setItem('auth', res?.access_token);
          localStorage.setItem('refresh', res?.refresh_token);
          localStorage.setItem('service', serviceName);
          navigate('');
        } else {
          setError(t('auth.error'));
        }
        setIsLoading(false);
      };
      loginRequest();
    },
    [serviceName, accessToken, t, navigate, setIsLoading, setIsLoggedIn]
  );

  const handlePaste = useCallback(() => {
    const doPaste = async () => {
      const text = await navigator.clipboard.readText();
      setAccessToken(text);
      handleLogin(text);
    };
    doPaste();
  }, [handleLogin]);

  useEffect(() => {
    const listener = (event) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        handleLogin();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [handleLogin]);

  return (
    <Box
      sx={{
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '70vh'
      }}
    >
      <AppBar position="fixed" sx={{ background: 'rgb(0, 1, 14)' }}>
        <Toolbar>
          <img src={NSLogo} style={{ width: 153, height: 35 }} />
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-around',
          width: 440
        }}
      >
        <Typography sx={{ alignSelf: 'flex-start', fontWeight: 500 }}>
          {t('auth.enter-token')}
        </Typography>
        <OutlinedInput
          defaultValue={accessToken}
          endAdornment={
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <IconButton
                onClick={handleClearClick}
                sx={{ visibility: accessToken ? 'visible' : 'hidden' }}
              >
                <Clear />
              </IconButton>
              <Button
                onClick={handlePaste}
                startIcon={<ContentPasteOutlinedIcon />}
                sx={{
                  '&:hover': { background: 'white' },
                  fontWeight: 400,
                  textTransform: 'unset',
                  color: 'rgba(0, 1, 14, 0.5)',
                  letterSpacing: '0.5px',
                  fontSize: 14
                }}
              >
                {t('auth.paste')}
              </Button>
            </Box>
          }
          onChange={handleChange}
          sx={{
            '.MuiTextField-root .MuiInputBase-root:hover:not(.Mui-disabled):before': {
              outline: 'none',
              border: 'none !important'
            },
            border: !!error ? '1px solid #C30' : 'initial',
            boxShadow: 1,
            width: 436,
            height: 44,
            background: 'white',
            fontSize: 14,
            m: '2px',
            input: {
              color: !!error ? '#C30' : 'rgba(0, 1, 14, 0.5)',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            },
            '&.Mui-focused .MuiIconButton-root': { color: 'primary.main' }
          }}
          value={accessToken}
        />

        {!!error && (
          <Typography sx={{ color: '#C30', alignSelf: 'flex-start', fontWeight: 500 }}>
            {error}
          </Typography>
        )}
        <Button
          disabled={!accessToken || !!error}
          onClick={() => handleLogin()}
          sx={{
            letterSpacing: '1.25px',
            fontWeight: 500,
            lineHeight: '36px',
            fontSize: 14,
            padding: '0 30px',
            bgcolor: '#0496FF',
            borderRadius: 1,
            mt: '40px'
          }}
          type="submit"
          variant="contained"
        >
          {t('auth.login')}
        </Button>
      </Box>
    </Box>
  );
}
